import { useState, useCallback, useRef, useEffect } from 'react'

import axios from 'axios'

import { apiErrorReport } from './apiErrorReport'
import useSentry from './useSentry'

function useApiPut() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const { captureException } = useSentry()
  const abortControllerRef = useRef(null)

  const putData = useCallback(({ path, data, onSuccess, onError }) => {
    // Create new controller for this request (don't abort previous to ensure completion)
    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    setError(null)

    const metaToken = document.getElementsByName('csrf-token')[0]
    const config = {
      headers: { 'X-CSRF-Token': metaToken.getAttribute('content') },
      withCredentials: true,
      signal: abortControllerRef.current.signal,
    }

    axios.put(path, data, config)
      .then(response => {
        setIsLoading(false)
        if (onSuccess) onSuccess(response)
      })
      .catch((internalError) => {
        // Silently ignore aborted requests - expected on navigation/unmount
        if (axios.isCancel(internalError) || internalError.code === 'ERR_CANCELED') {
          setIsLoading(false)
          return
        }

        setIsLoading(false)
        setError(internalError)
        captureException(internalError, apiErrorReport('PUT', path, internalError))

        if (onError) onError(internalError)
      })
  }, [captureException])

  // Cleanup: abort pending request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    putData,
    isLoading,
    error,
  }
}

export default useApiPut
